/**
 * One shared WebSocket to the chat realtime API (AWS API Gateway), opened
 * lazily for the first subscriber and closed when the last one leaves.
 *
 * `subscribe(handler)` returns an unsubscribe function. Handlers receive the
 * parsed event (`{ type: "message" | "typing" | "conversation.created" |
 * "conversation.updated", ... }`). When the backend reports realtime as
 * disabled, `subscribe` still works but nothing ever arrives — callers fall
 * back to polling (see `useChatRealtimeStatus`).
 */
import { useEffect, useState } from "react";
import chatService from "../services/chatService";

const handlers = new Set();
const statusListeners = new Set();
let socket = null;
let status = "idle"; // idle | connecting | open | disabled | closed
let reconnectTimer = null;
let reconnectDelay = 1000;
let pingTimer = null;
let wanted = false;

const setStatus = (next) => {
  if (status === next) return;
  status = next;
  statusListeners.forEach((fn) => fn(next));
};

const clearTimers = () => {
  if (reconnectTimer) clearTimeout(reconnectTimer);
  if (pingTimer) clearInterval(pingTimer);
  reconnectTimer = null;
  pingTimer = null;
};

const scheduleReconnect = () => {
  if (!wanted || reconnectTimer) return;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connect();
  }, reconnectDelay);
  reconnectDelay = Math.min(reconnectDelay * 2, 30000);
};

const connect = async () => {
  if (!wanted || socket) return;
  setStatus("connecting");
  let ticket;
  try {
    ticket = await chatService.getRealtimeTicket();
  } catch (e) {
    setStatus("closed");
    scheduleReconnect();
    return;
  }
  if (!ticket?.enabled || !ticket.url) {
    setStatus("disabled");
    return;
  }
  if (!wanted) return;
  const ws = new WebSocket(`${ticket.url}?ticket=${encodeURIComponent(ticket.ticket)}`);
  socket = ws;
  ws.onopen = () => {
    reconnectDelay = 1000;
    setStatus("open");
    // API Gateway drops idle sockets after 10 minutes; a ping every 5 keeps
    // the connection (and the DynamoDB row) warm.
    pingTimer = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ action: "ping" }));
    }, 5 * 60 * 1000);
  };
  ws.onmessage = (ev) => {
    let data;
    try {
      data = JSON.parse(ev.data);
    } catch {
      return;
    }
    handlers.forEach((fn) => {
      try {
        fn(data);
      } catch (e) {
        // One bad handler must not break delivery to the others.
        console.error("[chat realtime] handler failed", e);
      }
    });
  };
  ws.onclose = () => {
    if (socket === ws) socket = null;
    clearTimers();
    if (wanted) {
      setStatus("closed");
      scheduleReconnect();
    } else {
      setStatus("idle");
    }
  };
  ws.onerror = () => {
    try {
      ws.close();
    } catch {
      /* already closing */
    }
  };
};

const disconnect = () => {
  wanted = false;
  clearTimers();
  if (socket) {
    const ws = socket;
    socket = null;
    try {
      ws.close();
    } catch {
      /* ignore */
    }
  }
  setStatus("idle");
};

export const subscribe = (handler) => {
  handlers.add(handler);
  if (!wanted) {
    wanted = true;
    connect();
  }
  return () => {
    handlers.delete(handler);
    if (handlers.size === 0) disconnect();
  };
};

/** Current connection status, re-rendering on change. */
export const useChatRealtimeStatus = () => {
  const [value, setValue] = useState(status);
  useEffect(() => {
    statusListeners.add(setValue);
    setValue(status);
    return () => statusListeners.delete(setValue);
  }, []);
  return value;
};

/** Subscribe for the lifetime of the component. `handler` should be stable
 *  (wrap in useCallback) or events may be missed across re-subscribes. */
export const useChatRealtime = (handler, enabled = true) => {
  useEffect(() => {
    if (!enabled || !handler) return undefined;
    return subscribe(handler);
  }, [handler, enabled]);
};

export default useChatRealtime;
